import { Box, Typography, IconButton, ClickAwayListener, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AssetDrawerContent } from '../assetDrawerContent/AssetDrawerContent';
import { useCustomDashboardContext } from 'src/contexts/customDashboardContext/customDashboard.context';
import { TextLabelDrawerContent } from '../textLabelDrawerContent';
import { LiveTagDrawerContent } from '../liveTagDrawerContent';
import { RectangleDrawer } from '../rectangleDrawer/RectangleDrawer';
import { generateTitleBasedOnDrawerType } from '../../utils/generateTitleBasedOnDrawerType';
import { ImageUploadDrawer } from '../imageUploadDrawer/ImageUploadDrawer';
import { EditorMode } from '../../types';
import { TagDrawerContent } from '../tagDrawerContent/TagDrawerContent';
import { DASHBOARD_TEST_LABELS } from '../../constants';

export type ElementType = 'pump' | 'image' | 'valve' | string;

export type SelectedElement = {
  type: ElementType;
  data?: any;
};

type ConfigurationDrawerProps = {
  width?: number;
  mode?: EditorMode;
};

export const ConfigurationDrawer = ({ width = 350, mode }: ConfigurationDrawerProps) => {
  const {
    isDrawerOpened: open,
    closeDrawer: onClose,
    drawerType,
    deleteNode,
    selectedNode,
  } = useCustomDashboardContext();

  const isPreviewMode = mode === 'preview';

  const renderContent = () => {
    switch (drawerType) {
      case 'asset':
        return <AssetDrawerContent />;
      case 'textLabel':
        return <TextLabelDrawerContent />;
      case 'liveTagLabel':
        return <LiveTagDrawerContent />;
      case 'rectangle':
        return <RectangleDrawer />;
      case 'imageUpload':
        return <ImageUploadDrawer />;
      case 'tag':
        return <TagDrawerContent />;
    }
  };

  return (
    <ClickAwayListener
      onClickAway={(event) => {
        const target = event.target as Node;
        const portalClicked = (target as HTMLElement).closest('.MuiPopover-root');
        if (!portalClicked) {
          onClose();
        }
      }}
    >
      <Box
        key={selectedNode?.id ?? 'no-selection'}
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width,
          height: '100%',
          bgcolor: 'background.paper',
          boxShadow: 3,
          display: 'flex',
          flexDirection: 'column',
          zIndex: 10,
          transition: 'transform 0.2s ease, opacity 0.2s ease',
          transform: open && !isPreviewMode ? 'translateX(0)' : `translateX(${width}px)`,
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
          }}
          data-sm={DASHBOARD_TEST_LABELS.DRAWER_TITLE}
        >
          <Typography variant="h6">{generateTitleBasedOnDrawerType(drawerType)}</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {renderContent()}
          <Box sx={{ padding: '24px 0', display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              color="error"
              variant="outlined"
              onClick={() => {
                deleteNode(selectedNode?.id!);
              }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Box>
    </ClickAwayListener>
  );
};
